import { useState, useRef, useEffect } from 'react';
import { IoIosAddCircleOutline, IoIosInformationCircleOutline } from 'react-icons/io';
import { SiSalesforce } from 'react-icons/si';
import '../styles/DashboardSidePanel.css';

function DashboardSidePanel({
  userData,
  user,
  searches,
  activeSection,
  setActiveSection,
  handleLogout,
  isCollapsed,
  setIsCollapsed,
  isAdmin,
  impersonationEnabled,
  setImpersonationEnabled
}) {
  const hasProcessingSearch = searches.some(search => search.status === 'processing');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const userName = userData ? `${userData.first_name} ${userData.last_name}` : user?.email || 'User';

  return (
    <>
      {/* Toggle button - only visible when sidebar is collapsed */}
      {isCollapsed && (
        <button
          className="side-panel-toggle"
          onClick={() => setIsCollapsed(false)}
          aria-label="Open sidebar"
        >
          ☰
        </button>
      )}

      <aside className={`dashboard-side-panel ${isCollapsed ? 'collapsed' : ''}`}>
        {/* App branding with close button */}
        <div className="side-panel-branding">
          <span>Cappuccino</span>
          <button
            className="side-panel-close-btn"
            onClick={() => setIsCollapsed(true)}
            aria-label="Close sidebar"
          >
            ✕
          </button>
        </div>

        {/* User info section */}
        <div className="side-panel-user" ref={dropdownRef}>
          <button
            className="side-panel-user-button"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <div className="side-panel-user-avatar">
              {userData?.first_name?.[0] || user?.email?.[0] || 'U'}
            </div>
            <div className="side-panel-user-info">
              <span className="side-panel-user-name">{userName}</span>
              {userData?.remaining_credits !== undefined && (
                <span className={`side-panel-user-credits ${userData.remaining_credits === 0 ? 'no-credits' : ''}`}>
                  {userData.remaining_credits} credit{userData.remaining_credits !== 1 ? 's' : ''} remaining
                  {userData.remaining_credits === 0 && (
                    <span className="credits-info-tooltip">
                      <IoIosInformationCircleOutline className="credits-info-icon" />
                      <span className="credits-tooltip-text">Please contact the Cappuccino team for more credits.</span>
                    </span>
                  )}
                </span>
              )}
            </div>
          </button>

          {showDropdown && (
            <div className="side-panel-dropdown">
              {isAdmin && (
                <label className="side-panel-dropdown-toggle">
                  <span>View as user</span>
                  <input
                    type="checkbox"
                    checked={impersonationEnabled}
                    onChange={(e) => setImpersonationEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              )}
              <button
                className="side-panel-dropdown-item"
                onClick={handleLogout}
              >
                Sign out
              </button>
            </div>
          )}
        </div>

        {/* Navigation sections */}
        <nav className="side-panel-nav">
          <button
            className={`side-panel-nav-item ${activeSection === 'new-search' ? 'active' : ''}`}
            onClick={() => setActiveSection('new-search')}
          >
            <span className="side-panel-nav-text">New Search</span>
            <IoIosAddCircleOutline className="side-panel-nav-icon" />
          </button>

          <button
            className={`side-panel-nav-item ${activeSection === 'searches' ? 'active' : ''}`}
            onClick={() => setActiveSection('searches')}
          >
            <span className="side-panel-nav-text">
              Searches
              {hasProcessingSearch && <span className="search-processing-indicator" />}
            </span>
          </button>

          {/* List of searches under Searches section */}
          {activeSection === 'searches' && searches.length > 0 && (
            <div className="side-panel-search-list">
              {searches.slice(0, 5).map((search) => (
                <div key={search.id} className="side-panel-search-item">
                  <div className="side-panel-search-item-text">
                    {search.query?.substring(0, 30)}
                    {search.query?.length > 30 ? '...' : ''}
                  </div>
                  <div className={`side-panel-search-status status-${search.status}`}>
                    {search.status === 'created' ? '○' : search.status === 'processing' ? '◐' : '●'}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Leads tab - temporarily hidden
          <button
            className={`side-panel-nav-item ${activeSection === 'leads' ? 'active' : ''}`}
            onClick={() => setActiveSection('leads')}
          >
            <span className="side-panel-nav-text">Leads</span>
          </button>
          */}

          <button
            className={`side-panel-nav-item ${activeSection === 'templates' ? 'active' : ''}`}
            onClick={() => setActiveSection('templates')}
          >
            <span className="side-panel-nav-text">
              Templates
            </span>
          </button>

          <button
            className={`side-panel-nav-item ${activeSection === 'salesforce' ? 'active' : ''}`}
            onClick={() => setActiveSection('salesforce')}
          >
            <span className="side-panel-nav-text">
              <SiSalesforce className="side-panel-salesforce-icon" />
              Salesforce
            </span>
          </button>

          {/* Data Entry - Temporarily hidden
          {isAdmin && (
            <button
              className={`side-panel-nav-item ${activeSection === 'data-entry' ? 'active' : ''}`}
              onClick={() => setActiveSection('data-entry')}
            >
              <span className="side-panel-nav-text">Data Entry</span>
            </button>
          )}
          */}
        </nav>
      </aside>

      {/* Overlay for mobile */}
      {!isCollapsed && window.innerWidth < 768 && (
        <div
          className="side-panel-overlay"
          onClick={() => setIsCollapsed(true)}
        />
      )}
    </>
  );
}

export default DashboardSidePanel;
